import { create } from 'zustand'

// Per-object freshness counter. Any consumer that loads a design object
// (transaction, page, dashboard, …) can subscribe to its version and
// include it in a useEffect dep list — bumping the version forces a
// refetch even when the consuming component never unmounts (e.g. the
// embed preview inside the page designer canvas while the user round-
// trips through the transaction designer in the same browser tab).

type Key = string // `${objType}:${project}:${objName}`

function makeKey(objType: string, project: string, objName: string): Key {
  return `${objType}:${project}:${objName}`
}

interface ObjectVersionState {
  versions: Record<Key, number>
  bump: (objType: string, project: string, objName: string) => void
  getVersion: (objType: string, project: string, objName: string) => number
}

export const useObjectVersionStore = create<ObjectVersionState>((set, get) => ({
  versions: {},
  bump: (objType, project, objName) => {
    if (!project || !objName) return
    const key = makeKey(objType, project, objName)
    set((state) => ({
      versions: { ...state.versions, [key]: (state.versions[key] || 0) + 1 },
    }))
  },
  getVersion: (objType, project, objName) => {
    if (!project || !objName) return 0
    return get().versions[makeKey(objType, project, objName)] || 0
  },
}))

// Convenience hook: subscribe to one object's version. Returns 0 when
// the object has never been bumped — safe to use as a useEffect dep.
export function useObjectVersion(objType: string, project: string, objName: string): number {
  return useObjectVersionStore((s) => s.versions[makeKey(objType, project, objName)] || 0)
}
